import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ScoreBadge } from '../../components';
import { useAuth } from '../../context';
import { apiService } from '../../services';
import type { PromptDetails } from '../../types';
import { AiOutputPreview } from './AiOutputPreview';
import { PromptTextPreview } from './PromptTextPreview';
import { ideDisplayLabel } from './ideDisplay';
import { attachedRow, formatWhen } from './promptDetailFormat';
import { ScorePanel } from './ScorePanel';
import { isScoreDetailsV4 } from './explainScore';
import './PromptDetail.css';

export const PromptDetail: React.FC = () => {
  const { user } = useAuth();
  const { promptId } = useParams<{ promptId: string }>();
  const [prompt, setPrompt] = useState<PromptDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPromptDetails = async () => {
      if (!promptId) {
        setError('Invalid prompt ID');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.getPromptDetails(promptId);
        if (response.success) {
          setPrompt(response.data);
        } else {
          setError(response.message || 'Failed to load prompt details');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchPromptDetails();
  }, [promptId]);

  if (loading) {
    return (
      <div className="prompt-detail">
        <div className="prompt-detail__loading">
          <div className="prompt-detail__spinner" />
          <p>Loading prompt details...</p>
        </div>
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="prompt-detail">
        <div className="prompt-detail__error">
          <h2>Error</h2>
          <p>{error || 'Prompt not found'}</p>
          <Link to="/dashboard">Back to Prompts</Link>
        </div>
      </div>
    );
  }

  const when = formatWhen(prompt.timestamp);
  const ide = prompt.ide ? ideDisplayLabel(prompt.ide) : null;
  const v4 = isScoreDetailsV4(prompt.score_details) ? prompt.score_details : null;
  const attached = (prompt.artifacts && prompt.artifacts.length > 0)
    ? prompt.artifacts.map((art) => attachedRow(art.kind, art.name))
    : (v4?.derived?.file_details ?? []).map((file) => attachedRow(file.kind, file.name));

  const ownerLine =
    user?.role === 'leadership' && (prompt.user_name || prompt.email)
      ? (prompt.user_name || prompt.email)
      : null;

  const metaParts = [
    when.absolute,
    ide,
    prompt.repo_name ? `repo ${prompt.repo_name}` : null,
    ownerLine,
  ].filter(Boolean);

  return (
    <div className="prompt-detail">
      <header className="pd-head">
        <div>
          <Link className="pd-head__back" to="/dashboard">← Prompts</Link>
          <h1 className="pd-head__title">Prompt detail</h1>
          <p className="pd-head__who">{metaParts.join(' · ')}</p>
        </div>
        <div className="pd-head__score">
          <span className="explain__eyebrow">Overall score</span>
          <ScoreBadge score={prompt.overall_score} digits={1} large />
        </div>
      </header>

      <main className="pd-grid">
        <section className="prompt-col">
          <PromptTextPreview promptText={prompt.prompt_text} />
          <AiOutputPreview aiSummary={prompt.ai_summary} />
          <article className="prompt-col__card">
            <span className="explain__eyebrow">Attached</span>
            {attached.length === 0 ? (
              <p className="explain__muted">No files attached.</p>
            ) : (
              <ul className="prompt-col__files">
                {attached.map((item, index) => (
                  <li key={`${item.pill}-${index}`}>
                    <span>{item.name}</span>
                    <span className="pill">{item.pill}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
          <article className="prompt-col__card">
            <span className="explain__eyebrow">Session</span>
            <dl className="prompt-col__session">
              <div><dt>IDE</dt><dd>{ide || '—'}</dd></div>
              <div><dt>Prompts in session</dt><dd>{prompt.session_turn_count ?? 1}</dd></div>
            </dl>
          </article>
        </section>
        <ScorePanel prompt={prompt} />
      </main>
    </div>
  );
};

export default PromptDetail;
